/**
 * Text sanitizer for BCP form data
 *
 * Removes or replaces problematic characters that appear in PDFs/DOCX documents,
 * particularly checkbox characters (☒, ☑, □, ■) and invisible Unicode characters
 * (line separators, zero-width spaces) that may be inadvertently included in
 * form input data.
 */

// Checkbox and box-drawing characters that should be removed
const CHECKBOX_CHARS = [
  '\u2612', // ☒ BALLOT BOX WITH X
  '\u2611', // ☑ BALLOT BOX WITH CHECK
  '\u2610', // ☐ BALLOT BOX
  '\u25A1', // □ WHITE SQUARE
  '\u25A0', // ■ BLACK SQUARE
  '\u25FB', // ◻ WHITE MEDIUM SQUARE
  '\u25FC', // ◼ BLACK MEDIUM SQUARE
  '\u25FD', // ◽ WHITE MEDIUM SMALL SQUARE
  '\u25FE', // ◾ BLACK MEDIUM SMALL SQUARE
];

// Create a regex pattern to match all checkbox characters
const CHECKBOX_PATTERN = new RegExp(`[${CHECKBOX_CHARS.join('')}]`, 'g');

// Invisible Unicode characters that cause rendering issues
const INVISIBLE_CHARS: Record<string, string> = {
  '\u2028': '\n', // LINE SEPARATOR → newline
  '\u2029': '\n', // PARAGRAPH SEPARATOR → newline
  '\u00A0': ' ',  // NO-BREAK SPACE → normal space
  '\u200B': '',   // ZERO WIDTH SPACE → remove
  '\u200C': '',   // ZERO WIDTH NON-JOINER → remove
  '\u200D': '',   // ZERO WIDTH JOINER → remove
  '\uFEFF': '',   // ZERO WIDTH NO-BREAK SPACE (BOM) → remove
};

/**
 * Sanitize text content by removing problematic characters
 *
 * @param text - Input text to sanitize (can be null or undefined)
 * @param removeCheckbox - Whether to remove checkbox characters (default: true)
 * @param normalizeUnicode - Whether to normalize invisible Unicode chars (default: true)
 * @returns Sanitized text string (empty string if input is null/undefined)
 */
export const sanitizeText = (
  text: string | null | undefined,
  removeCheckbox: boolean = true,
  normalizeUnicode: boolean = true
): string => {
  if (text === null || text === undefined || typeof text !== 'string') {
    return '';
  }

  let sanitized = text;

  // Normalize invisible Unicode characters first (before checkbox removal)
  if (normalizeUnicode) {
    for (const [char, replacement] of Object.entries(INVISIBLE_CHARS)) {
      if (sanitized.includes(char)) {
        sanitized = sanitized.replace(new RegExp(char, 'g'), replacement);
        console.debug(`Replaced ${JSON.stringify(char)} with ${JSON.stringify(replacement)} in text`);
      }
    }
  }

  if (removeCheckbox) {
    // Remove checkbox characters
    const originalLength = sanitized.length;
    sanitized = sanitized.replace(CHECKBOX_PATTERN, '');
    const removedCount = originalLength - sanitized.length;

    if (removedCount > 0) {
      console.info(`Removed ${removedCount} checkbox character(s) from text`);
    }
  }

  return sanitized;
};

/**
 * Recursively sanitize all string values in an object
 *
 * @param data - Object containing form data
 * @param removeCheckbox - Whether to remove checkbox characters (default: true)
 * @param normalizeUnicode - Whether to normalize invisible Unicode chars (default: true)
 * @returns New object with sanitized string values
 */
export const sanitizeObject = <T extends Record<string, any>>(
  data: T,
  removeCheckbox: boolean = true,
  normalizeUnicode: boolean = true
): T => {
  const sanitized: Record<string, any> = {};

  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string') {
      sanitized[key] = sanitizeText(value, removeCheckbox, normalizeUnicode);
    } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      sanitized[key] = sanitizeObject(value, removeCheckbox, normalizeUnicode);
    } else if (Array.isArray(value)) {
      sanitized[key] = value.map((item) =>
        typeof item === 'string' ? sanitizeText(item, removeCheckbox, normalizeUnicode) : item
      );
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized as T;
};

/**
 * Check if text contains checkbox characters
 *
 * @param text - Text to check
 * @returns True if text contains checkbox characters
 */
export const containsCheckboxChars = (text: string | null | undefined): boolean => {
  if (!text) return false;
  // Create a new regex without global flag for test() to work correctly
  const checkPattern = new RegExp(`[${CHECKBOX_CHARS.join('')}]`);
  return checkPattern.test(text);
};
